import type { PloscicaPregleda, StolpecPorocila, TockaPregleda, VrsticaPorocila } from '@koda/shared';
import type { JSX } from 'react';

import { decimalno, evri } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import { useTelefon } from '../lib/telefon';
import { korakOsi, STOLPCI_SPLET, STOLPCI_TELEFON, tisoc, zaobljen } from './GrafPrihodkov';
import { celicaDelov, naTipko } from './kazalniki-skupno';
import type { Celica } from './PanelRazclenitve';

/**
 * Grafi zavihka Podrobno (zasnova »Podrobno – splet«): celotni dobiček po
 * mesecih, stanje denarja in profitna marža. Vsaka točka, ki je vsota knjižb
 * (dobiček meseca, stanje denarja na dan), odpre razčlenitev; profitna marža
 * je razmerje in ni klikljiva (6.3).
 */

const MODRA = '#256abf';
const RDECA = '#a4262c';
const CRTA = '#e9e7e1';
const NICLA = '#cfccc4';
const TIHO = '#6f6c65';
const BESEDILO = '#1c1b19';

/** Os: lep korak, zgornja in spodnja meja (vključno z 0) in razdelki. */
function os(vrednosti: number[]): { korak: number; najvec: number; najmanj: number; razdelki: number[] } {
  const korak = korakOsi(Math.max(0, ...vrednosti) - Math.min(0, ...vrednosti));
  const najvec = Math.max(korak, Math.ceil(Math.max(0, ...vrednosti) / korak) * korak);
  const najmanj = Math.floor(Math.min(0, ...vrednosti) / korak) * korak;
  const razdelki = Array.from({ length: Math.round((najvec - najmanj) / korak) + 1 }, (_, i) => najmanj + i * korak);
  return { korak, najvec, najmanj, razdelki };
}

const mesecKratko = (meseci: string[], m: number): string => (meseci[m - 1] ?? String(m)).slice(0, 3);

interface LastnostiDobicka {
  ploscica: PloscicaPregleda;
  leto: number;
  primerjalno: number | null;
  naCelico: (celica: Celica) => void;
}

/**
 * Celotni dobiček po mesecih: stolpec tekočega leta (modro dobiček, rdeče
 * izguba pod ničlo), črtica primerjalnega leta za isti mesec, oznaki ob
 * najvišjem in najnižjem stolpcu.
 */
export function GrafDobicka({ ploscica: p, leto, primerjalno, naCelico }: LastnostiDobicka): JSX.Element {
  const b = useBesedila();
  const m = useTelefon() ? STOLPCI_TELEFON : STOLPCI_SPLET;
  const t = p.poMesecih.t;
  const zadnji = t[t.length - 1]?.mesec ?? 0;
  const t1 = primerjalno === null ? [] : p.poMesecih.t1.filter((x) => x.mesec <= zadnji);
  const { najvec, najmanj, razdelki } = os([...t, ...t1].map((x) => Number(x.vrednost)));
  const y = (v: number): number => m.vrh + ((najvec - v) / (najvec - najmanj)) * (m.nicla - m.vrh);
  const sirinaMeseca = (m.desno - m.levo) / Math.max(t.length, 1);
  const sredina = (i: number): number => m.levo + (i + 0.5) * sirinaMeseca;
  const w = Math.round(m.stolpec * 1.6);
  const vrednosti = t.map((x) => Number(x.vrednost));
  const iNajvec = vrednosti.indexOf(Math.max(...vrednosti));
  const iNajmanj = vrednosti.indexOf(Math.min(...vrednosti));
  const odpri = (tc: TockaPregleda, l: number) => (): void => {
    naCelico(celicaDelov(p.naziv, `${b.meseci[tc.mesec - 1] ?? String(tc.mesec)} ${String(l)}`, tc.vrednost, tc.deli));
  };
  const naslov = (tc: TockaPregleda, l: number): string => `${b.meseci[tc.mesec - 1] ?? String(tc.mesec)} ${String(l)}: ${evri(tc.vrednost)}`;

  return (
    <section className="kz-karta kz-graf-prihodkov" aria-label={b.pk.grafDobicka}>
      <div className="kz-karta-glava">
        <div className="kz-karta-naslov">
          <h2>{b.pk.grafDobicka}</h2>
          <div className="kz-karta-podnaslov">{b.pk.grafDobickaPodnaslov(leto, primerjalno)}</div>
        </div>
        <div className="kz-legenda">
          <span>
            <span className="kz-kvadrat" style={{ background: MODRA }} />
            {b.pk.dobicek}
          </span>
          <span>
            <span className="kz-kvadrat" style={{ background: RDECA }} />
            {b.pk.izguba}
          </span>
          {primerjalno !== null && (
            <span>
              <span className="kz-crtica" />
              {primerjalno}
            </span>
          )}
        </div>
      </div>
      <svg className="kz-stolpci" viewBox={`0 0 ${String(m.sirina)} ${String(m.visina)}`} role="img" aria-label={b.pk.grafDobicka}>
        {razdelki.map((v) => (
          <g key={v}>
            <line x1={m.levo} x2={m.desno} y1={y(v).toFixed(1)} y2={y(v).toFixed(1)} stroke={v === 0 ? NICLA : CRTA} strokeWidth="1" />
            <text x={m.levo - 8} y={(y(v) + 4).toFixed(1)} textAnchor="end" fontSize="11" fill={TIHO}>
              {tisoc(v)}
            </text>
          </g>
        ))}
        {t.map((tc, i) => {
          const v = Number(tc.vrednost);
          const x = sredina(i) - w / 2;
          const lani = t1.find((z) => z.mesec === tc.mesec);
          return (
            <g key={tc.mesec}>
              <path
                className="kz-stolpec"
                d={
                  v < 0
                    ? `M${x.toFixed(1)},${y(0).toFixed(1)} H${(x + w).toFixed(1)} V${y(v).toFixed(1)} H${x.toFixed(1)} Z`
                    : zaobljen(x, y(v), w, y(0))
                }
                fill={v < 0 ? RDECA : MODRA}
                role="button"
                tabIndex={0}
                aria-label={naslov(tc, leto)}
                onClick={odpri(tc, leto)}
                onKeyDown={naTipko(odpri(tc, leto))}
              >
                <title>{naslov(tc, leto)}</title>
              </path>
              {lani !== undefined && primerjalno !== null && (
                <g
                  className="kz-crtica-lani"
                  role="button"
                  tabIndex={0}
                  aria-label={naslov(lani, primerjalno)}
                  onClick={odpri(lani, primerjalno)}
                  onKeyDown={naTipko(odpri(lani, primerjalno))}
                >
                  <title>{naslov(lani, primerjalno)}</title>
                  {/* Širši nevidni pas, da je črtico mogoče zadeti. */}
                  <rect x={x - 5} y={y(Number(lani.vrednost)) - 5} width={w + 10} height={10} fill="transparent" />
                  <line x1={x - 5} x2={x + w + 5} y1={y(Number(lani.vrednost)).toFixed(1)} y2={y(Number(lani.vrednost)).toFixed(1)} stroke={BESEDILO} strokeWidth="2" />
                </g>
              )}
              <text x={sredina(i).toFixed(1)} y={m.nicla + 22} textAnchor="middle" fontSize="12" fill="#5d5b55">
                {mesecKratko(b.meseci, tc.mesec)}
              </text>
            </g>
          );
        })}
        {t[iNajvec] !== undefined && Number(t[iNajvec].vrednost) > 0 && (
          <text x={sredina(iNajvec).toFixed(1)} y={(y(Number(t[iNajvec].vrednost)) - 8).toFixed(1)} textAnchor="middle" fontSize="11" fontWeight="600" fill={BESEDILO}>
            {tisoc(Number(t[iNajvec].vrednost))}
          </text>
        )}
        {t[iNajmanj] !== undefined && Number(t[iNajmanj].vrednost) < 0 && (
          <text x={sredina(iNajmanj).toFixed(1)} y={(y(Number(t[iNajmanj].vrednost)) + 16).toFixed(1)} textAnchor="middle" fontSize="11" fontWeight="600" fill={BESEDILO}>
            {tisoc(Number(t[iNajmanj].vrednost))}
          </text>
        )}
      </svg>
    </section>
  );
}

interface MereCrte {
  sirina: number;
  visina: number;
  levo: number;
  desno: number;
  vrh: number;
  dno: number;
}
export const CRTA_SPLET: MereCrte = { sirina: 780, visina: 168, levo: 44, desno: 766, vrh: 18, dno: 138 };
export const CRTA_TELEFON: MereCrte = { sirina: 342, visina: 168, levo: 40, desno: 330, vrh: 18, dno: 138 };

/**
 * Stanje denarja: 1. 1. in konec vsakega meseca (ploščica »Denar«), oznaki
 * ob najvišjem in zadnjem stanju. Točka odpre knjižbe, iz katerih je stanje
 * (od otvoritve do tega dne).
 */
export function GrafDenarja({ ploscica: p, leto, naCelico }: { ploscica: PloscicaPregleda; leto: number; naCelico: (celica: Celica) => void }): JSX.Element {
  const b = useBesedila();
  const m = useTelefon() ? CRTA_TELEFON : CRTA_SPLET;
  const tocke = [
    ...(p.primerjava === null ? [] : [{ oznaka: b.pk.zacetekLeta, naslov: `1. 1. ${String(leto)}`, vrednost: p.primerjava, deli: p.deliPrimerjave }]),
    ...p.poMesecih.t.map((tc) => ({
      oznaka: mesecKratko(b.meseci, tc.mesec),
      naslov: `${String(new Date(Date.UTC(leto, tc.mesec, 0)).getUTCDate())}. ${String(tc.mesec)}. ${String(leto)}`,
      vrednost: tc.vrednost,
      deli: tc.deli,
    })),
  ];
  const { najvec, najmanj, razdelki } = os(tocke.map((t) => Number(t.vrednost)));
  const y = (v: number): number => m.vrh + ((najvec - v) / (najvec - najmanj)) * (m.dno - m.vrh);
  const x = (i: number): number => m.levo + 12 + (i * (m.desno - m.levo - 24)) / Math.max(tocke.length - 1, 1);
  const vrednosti = tocke.map((t) => Number(t.vrednost));
  const iNajvec = vrednosti.indexOf(Math.max(...vrednosti));
  const iZadnji = tocke.length - 1;
  const oznaka = (i: number): JSX.Element => (
    <text
      key={`oznaka-${String(i)}`}
      x={(x(i) + (i === iZadnji ? -4 : 4)).toFixed(1)}
      y={(y(vrednosti[i] ?? 0) - 8).toFixed(1)}
      textAnchor={i === iZadnji ? 'end' : 'start'}
      fontSize="11"
      fontWeight="600"
      fill={BESEDILO}
    >
      {tisoc(vrednosti[i] ?? 0)}
    </text>
  );

  return (
    <svg className="kz-stolpci" viewBox={`0 0 ${String(m.sirina)} ${String(m.visina)}`} role="img" aria-label={b.pk.stanjeDenarja}>
      {razdelki.map((v) => (
        <g key={v}>
          <line x1={m.levo} x2={m.desno} y1={y(v).toFixed(1)} y2={y(v).toFixed(1)} stroke={v === 0 ? NICLA : CRTA} strokeWidth="1" />
          <text x={m.levo - 8} y={(y(v) + 4).toFixed(1)} textAnchor="end" fontSize="11" fill={TIHO}>
            {tisoc(v)}
          </text>
        </g>
      ))}
      <polyline
        points={tocke.map((t, i) => `${x(i).toFixed(1)},${y(Number(t.vrednost)).toFixed(1)}`).join(' ')}
        fill="none"
        stroke={MODRA}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {tocke.map((t, i) => (
        <g
          key={t.naslov}
          className="kz-tocka"
          role="button"
          tabIndex={0}
          aria-label={`${b.pk.stanjeDenarja} ${t.naslov}: ${evri(t.vrednost)}`}
          onClick={() => {
            naCelico(celicaDelov(p.naziv, t.naslov, t.vrednost, t.deli));
          }}
          onKeyDown={naTipko(() => {
            naCelico(celicaDelov(p.naziv, t.naslov, t.vrednost, t.deli));
          })}
        >
          <title>{`${b.pk.stanjeDenarja} ${t.naslov}: ${evri(t.vrednost)}`}</title>
          <circle cx={x(i).toFixed(1)} cy={y(Number(t.vrednost)).toFixed(1)} r="10" fill="transparent" />
          <circle cx={x(i).toFixed(1)} cy={y(Number(t.vrednost)).toFixed(1)} r="4" fill={MODRA} />
          <text x={x(i).toFixed(1)} y={m.dno + 20} textAnchor="middle" fontSize="12" fill="#5d5b55">
            {t.oznaka}
          </text>
        </g>
      ))}
      {tocke.length > 0 && oznaka(iNajvec)}
      {tocke.length > 1 && iZadnji !== iNajvec && oznaka(iZadnji)}
    </svg>
  );
}

export const MARZA_SPLET: MereCrte = { sirina: 420, visina: 168, levo: 40, desno: 412, vrh: 18, dno: 138 };

/**
 * Profitna marža po mesecih: stolpec za vsak mesec s prodajno vrednostjo in
 * črtkana črta marže obdobja (razlika v ceni obdobja ÷ prodajna vrednost
 * obdobja; ni navadno povprečje mesečnih marž). Razmerje ni vsota knjižb,
 * zato ni klikljivo; zneski pod grafom so.
 */
export function GrafMarze({ marza, stolpci }: { marza: VrsticaPorocila; stolpci: StolpecPorocila[] }): JSX.Element {
  const b = useBesedila();
  const m = useTelefon() ? CRTA_TELEFON : MARZA_SPLET;
  const tocke = stolpci.map((s, i) => ({ mesec: s.obdobje, naziv: s.naziv, vrednost: marza.zneski[i] ?? '' }));
  // Marža obdobja: razlika v ceni obdobja ÷ prodajna vrednost obdobja (tehtana
  // s prodajo), ne navadno povprečje mesečnih marž.
  const obdobja = marza.skupaj === '' ? null : Number(marza.skupaj);
  const vse = [...tocke.filter((t) => t.vrednost !== '').map((t) => Number(t.vrednost)), ...(obdobja === null ? [] : [obdobja])];
  const { korak, najvec, najmanj, razdelki } = os(vse);
  const y = (v: number): number => m.vrh + ((najvec - v) / (najvec - najmanj)) * (m.dno - m.vrh);
  const sirinaMeseca = (m.desno - m.levo) / Math.max(tocke.length, 1);
  const sredina = (i: number): number => m.levo + (i + 0.5) * sirinaMeseca;
  const w = Math.min(22, sirinaMeseca * 0.5);
  // Decimalke osi po koraku, da so razdelki pri nizki marži (npr. 0,2 %) različni.
  const decimalkeOsi = korak >= 1 ? 0 : korak >= 0.1 ? 1 : 2;
  const odst = (v: number): string => `${decimalno(String(v), decimalkeOsi)} %`;

  return (
    <svg className="kz-stolpci" viewBox={`0 0 ${String(m.sirina)} ${String(m.visina)}`} role="img" aria-label={marza.naziv}>
      {razdelki.map((v) => (
        <g key={v}>
          <line x1={m.levo} x2={m.desno} y1={y(v).toFixed(1)} y2={y(v).toFixed(1)} stroke={v === 0 ? NICLA : CRTA} strokeWidth="1" />
          <text x={m.levo - 8} y={(y(v) + 4).toFixed(1)} textAnchor="end" fontSize="11" fill={TIHO}>
            {odst(v)}
          </text>
        </g>
      ))}
      {tocke.map((t, i) => {
        const v = Number(t.vrednost);
        const x = sredina(i) - w / 2;
        return (
          <g key={t.mesec}>
            {t.vrednost !== '' && (
              <path
                d={v < 0 ? `M${x.toFixed(1)},${y(0).toFixed(1)} H${(x + w).toFixed(1)} V${y(v).toFixed(1)} H${x.toFixed(1)} Z` : zaobljen(x, y(v), w, y(0))}
                fill={v < 0 ? RDECA : MODRA}
              >
                <title>{`${t.naziv}: ${decimalno(t.vrednost, 1)} %`}</title>
              </path>
            )}
            <text x={sredina(i).toFixed(1)} y={m.dno + 20} textAnchor="middle" fontSize="12" fill="#5d5b55">
              {mesecKratko(b.meseci, t.mesec)}
            </text>
          </g>
        );
      })}
      {obdobja !== null && (
        <g>
          <line x1={m.levo} x2={m.desno} y1={y(obdobja).toFixed(1)} y2={y(obdobja).toFixed(1)} stroke={BESEDILO} strokeWidth="1" strokeDasharray="4 3" />
          {/* Bel obris, da je napis berljiv tudi nad stolpcem. */}
          <text x={m.desno} y={(y(obdobja) - 6).toFixed(1)} textAnchor="end" fontSize="11" fontWeight="600" fill={BESEDILO} stroke="#fff" strokeWidth="3" paintOrder="stroke">
            {`${b.pk.marzaObdobja} ${decimalno(marza.skupaj, 1)} %`}
          </text>
        </g>
      )}
    </svg>
  );
}
